import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Download,
  Printer,
  ArrowLeft,
  CheckCircle,
  XCircle,
  BarChart2,
  Clock,
  User,
  Package,
  Calendar,
  Building2
} from 'lucide-react';

const BatchReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock fetch batch data
    const fetchBatchData = async () => {
      setLoading(true);
      // Simulate API call
      setTimeout(() => {
        const mockBatch = {
          id,
          batchNumber: `CK1M2024${String(Math.floor(Math.random() * 10000)).padStart(4, '0')}`,
          product: 'Sauce Base',
          location: 'Central Kitchen 1',
          date: new Date().toISOString(),
          shift: 'Morning',
          quantity: 250,
          chef: 'Ahmed Ali',
          team: ['Sara Khan', 'Mohammed Hassan'],
          qualityChecks: [
            { parameter: 'Temperature', value: '72', unit: '°C', status: 'pass', target: '70-75' },
            { parameter: 'pH Level', value: '4.2', unit: 'pH', status: 'pass', target: '4.0-4.5' },
            { parameter: 'Moisture Content', value: '65', unit: '%', status: 'pass', target: '60-70' }
          ],
          notes: 'Standard production batch with no deviations',
          temperature: '22',
          humidity: '45',
          expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'Active',
          ingredients: [
            { name: 'Tomato Paste', quantity: '50', unit: 'kg', lot: 'TP24001' },
            { name: 'Spice Mix', quantity: '5', unit: 'kg', lot: 'SM24003' },
            { name: 'Salt', quantity: '2', unit: 'kg', lot: 'SL24002' }
          ],
          history: [
            {
              date: new Date().toISOString(),
              action: 'Batch Created',
              user: 'Ahmed Ali',
              details: 'Initial batch creation'
            },
            {
              date: new Date().toISOString(),
              action: 'Quality Check',
              user: 'Sara Khan',
              details: 'All parameters within specifications'
            }
          ],
          productionMetrics: {
            startTime: '08:00',
            endTime: '10:30',
            totalTime: '2.5 hours',
            yield: '98%',
            wastage: '2%'
          }
        };
        setBatch(mockBatch);
        setLoading(false);
      }, 1000);
    };

    fetchBatchData();
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // Handle PDF generation and download
    console.log('Downloading report...');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="min-h-screen bg-white p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Batch Not Found</h2>
          <p className="text-gray-500 mt-2">The requested batch could not be found.</p>
          <button
            onClick={() => navigate('/batch-control')}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Not printed */}
      <div className="print:hidden bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/batch-control/${id}`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-semibold">Batch Report</h1>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handlePrint}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print Report
              </button>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-bold mb-2">Batch Production Report</h1>
          <p className="text-gray-500">Batch Number: {batch.batchNumber}</p>
        </div>

        {/* Basic Information */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Basic Information</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500">Product</p>
              <p className="font-medium">{batch.product}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Location</p>
              <p className="font-medium">{batch.location}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Production Date</p>
              <p className="font-medium">{new Date(batch.date).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Expiry Date</p>
              <p className="font-medium">{new Date(batch.expiryDate).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Shift</p>
              <p className="font-medium">{batch.shift}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Quantity Produced</p>
              <p className="font-medium">{batch.quantity} units</p>
            </div>
          </div>
        </div>

        {/* Production Team */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Production Team</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">Chef in Charge</p>
              <p className="font-medium">{batch.chef}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Team Members</p>
              <ul className="list-disc list-inside">
                {batch.team.map((member, index) => (
                  <li key={index} className="font-medium">{member}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Quality Checks */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Quality Checks</h2>
          <table className="w-full">
            <thead>
              <tr className="text-left">
                <th className="pb-2">Parameter</th>
                <th className="pb-2">Target Range</th>
                <th className="pb-2">Actual Value</th>
                <th className="pb-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {batch.qualityChecks.map((check, index) => (
                <tr key={index} className="border-t">
                  <td className="py-2">{check.parameter}</td>
                  <td className="py-2">{check.target}</td>
                  <td className="py-2">
                    {check.value} {check.unit}
                  </td>
                  <td className="py-2">
                    <div className="flex items-center">
                      {check.status === 'pass' ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-rose-500" />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Ingredients and Materials */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Ingredients and Materials</h2>
          <table className="w-full">
            <thead>
              <tr className="text-left">
                <th className="pb-2">Item</th>
                <th className="pb-2">Quantity</th>
                <th className="pb-2">Unit</th>
                <th className="pb-2">Lot Number</th>
              </tr>
            </thead>
            <tbody>
            {batch.ingredients.map((ingredient, index) => (
                <tr key={index} className="border-t">
                  <td className="py-2">{ingredient.name}</td>
                  <td className="py-2">{ingredient.quantity}</td>
                  <td className="py-2">{ingredient.unit}</td>
                  <td className="py-2">{ingredient.lot}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Production Metrics */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Production Metrics</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500">Start Time</p>
              <p className="font-medium">{batch.productionMetrics.startTime}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">End Time</p>
              <p className="font-medium">{batch.productionMetrics.endTime}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Production Time</p>
              <p className="font-medium">{batch.productionMetrics.totalTime}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Production Yield</p>
              <p className="font-medium">{batch.productionMetrics.yield}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Wastage</p>
              <p className="font-medium">{batch.productionMetrics.wastage}</p>
            </div>
          </div>
        </div>

        {/* Batch History */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Batch History</h2>
          <table className="w-full">
            <thead>
              <tr className="text-left">
                <th className="pb-2">Date</th>
                <th className="pb-2">Action</th>
                <th className="pb-2">User</th>
                <th className="pb-2">Details</th>
              </tr>
            </thead>
            <tbody>
              {batch.history.map((event, index) => (
                <tr key={index} className="border-t">
                  <td className="py-2">{new Date(event.date).toLocaleString()}</td>
                  <td className="py-2">{event.action}</td>
                  <td className="py-2">{event.user}</td>
                  <td className="py-2">{event.details}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes and Comments */}
        {batch.notes && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 pb-2 border-b">Notes</h2>
            <p className="text-gray-700">{batch.notes}</p>
          </div>
        )}

        {/* Report Footer */}
        <div className="mt-12 pt-8 border-t">
          <div className="grid grid-cols-2 gap-8">
            <div>
              <p className="text-sm text-gray-500">Report Generated By</p>
              <p className="font-medium">System Generated</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Generated On</p>
              <p className="font-medium">{new Date().toLocaleString()}</p>
            </div>
          </div>
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>This is a system-generated report for batch {batch.batchNumber}</p>
            <p>© {new Date().getFullYear()} Your Company Name</p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 2cm;
          }
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default BatchReport;